import React, { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Hammer, Wrench, Ruler, Grid, Briefcase, Paintbrush, Settings, ArrowLeft, Check, Phone, Mail } from 'lucide-react';
import { companyData } from '../data';

const iconMap = {
  Hammer: Hammer,
  Wrench: Wrench,
  Construction: Ruler,
  Grid: Grid,
  Briefcase: Briefcase,
  Paintbrush: Paintbrush,
  Settings: Settings
};

// Map services to specific asset images to make each page unique and visually stunning
const serviceImages = {
  renovering: "https://assets.auxodev.dk/assets/39491458-c5e4-4280-8295-09ca3e3ec516/d087e998-bda0-4ad6-9b80-f40d1c0ad633.jpg",
  vvs: "https://assets.auxodev.dk/assets/39491458-c5e4-4280-8295-09ca3e3ec516/440565f6-c8f2-485d-b181-302b3b5b8ee3.jpg",
  toemrer: "https://assets.auxodev.dk/assets/39491458-c5e4-4280-8295-09ca3e3ec516/c43cb3f1-b606-42f4-b6ff-93a00c8e370d.jpg",
  murer: "https://assets.auxodev.dk/assets/39491458-c5e4-4280-8295-09ca3e3ec516/25a8be90-7fd7-4851-b64f-45aac4b57200.jpg",
  maler: "https://assets.auxodev.dk/assets/39491458-c5e4-4280-8295-09ca3e3ec516/dc3f9701-3f28-456e-ae0c-97ff3192f4ca.jpg",
  totalentreprise: "https://assets.auxodev.dk/assets/39491458-c5e4-4280-8295-09ca3e3ec516/1f501778-72bf-46d1-ab94-5b171a85b464.jpg",
  handyman: "https://assets.auxodev.dk/assets/39491458-c5e4-4280-8295-09ca3e3ec516/c1d5f860-5a63-45a2-a251-e5177a9b5567.jpg"
};

// Detailed bullet points for each service to make the subpages rich and informative
const serviceDetails = {
  renovering: [
    "Totalrenovering af huse, lejligheder og erhvervsejendomme",
    "Modernisering af køkken, bad og opholdsrum",
    "Optimering af planløsninger og vægnedrivning",
    "Energirenovering, efterisolering og udskiftning af døre/vinduer",
    "Professionel rådgivning fra idé til færdigt resultat"
  ],
  vvs: [
    "Installation og udskiftning af toiletter, håndvaske og armaturer",
    "Renovering og etablering af badeværelser",
    "Varmeinstallationer, herunder gulvvarme og radiatorer",
    "Reparation af rørskader og akutte VVS-problemer",
    "Vedligeholdelse og serviceaftaler på VVS-installationer"
  ],
  toemrer: [
    "Nye træterrasser, carporte og skure i høj kvalitet",
    "Udskiftning og renovering af tagkonstruktioner og spær",
    "Montering af døre, vinduer, lofter og gulve",
    "Opbygning af skillevægge og gipskonstruktioner",
    "Specialopgaver og snedkerarbejde tilpasset dine ønsker"
  ],
  murer: [
    "Flisearbejde og klinker i badeværelser, køkkener og bryggers",
    "Facaderenovering, pudsning og vandskuring",
    "Opbygning af råhuse, tilbygninger og murede konstruktioner",
    "Reparation af sætningsskader og fugearbejde",
    "Støbning af gulve og fundamenter"
  ],
  maler: [
    "Indendørs malerarbejde af vægge, lofter, døre og karme",
    "Udendørs maling af facader, træværk og udhæng",
    "Fuldspatling, opsætning af filt, væv og tapet",
    "Grundig klargøring og slibning for det perfekte resultat",
    "Miljøvenlige kvalitetsmalinger med høj dækkeevne"
  ],
  totalentreprise: [
    "Komplet styring og koordinering af hele byggeprojektet",
    "Tæt samarbejde med autoriserede el-installatører og andre faggrupper",
    "Én fast kontaktperson gennem hele forløbet",
    "Overholdelse af tidsplaner, budgetter og aftaler",
    "Kvalitetssikring af alle udførte opgaver"
  ],
  handyman: [
    "Ophængning af hylder, billeder, TV og gardiner",
    "Samling og montering af møbler og inventar",
    "Mindre reparationer og justeringer i boligen",
    "Udskiftning af pærer, pakninger og småting",
    "Hurtig og effektiv hjælp til de opgaver, du ikke selv når"
  ]
};

export default function ServiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  // Find the current service
  const service = companyData.services.find(s => s.id === id);

  // Scroll to top on mount or when service ID changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  if (!service) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-4">
        <h2 className="text-3xl font-bold text-white mb-4 uppercase">Ydelsen blev ikke fundet</h2>
        <p className="text-gray-400 mb-8">Den ydelse, du leder efter, findes desværre ikke eller er blevet flyttet.</p>
        <Link
          to="/"
          className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 font-bold uppercase tracking-wider text-sm transition-colors rounded-none"
        >
          Tilbage til forsiden
        </Link>
      </div>
    );
  }

  const IconComponent = iconMap[service.icon] || Hammer;
  const imageUrl = serviceImages[service.id] || serviceImages.renovering;
  const bullets = serviceDetails[service.id] || [];
  const otherServices = companyData.services.filter(s => s.id !== service.id);

  return (
    <div className="bg-[#111111] text-white min-h-screen">
      {/* Hero Banner for Service */}
      <section className="relative py-20 md:py-32 overflow-hidden border-b border-gray-900">
        <div className="absolute inset-0 z-0">
          <img
            src={imageUrl}
            alt={service.title}
            className="w-full h-full object-cover opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#111111] via-[#111111]/80 to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-6 text-sm font-semibold uppercase tracking-wider"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Tilbage til forsiden</span>
          </Link>

          <div className="flex flex-col md:flex-row md:items-center gap-6">
            <div>
              <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight text-white uppercase">
                {service.title}
              </h1>
              <p className="mt-4 text-lg md:text-xl text-gray-300 max-w-3xl font-light leading-relaxed">
                {service.description}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Detailed Content Section */}
      <section className="py-16 md:py-24 bg-white text-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            
            {/* Left Column: Details & Bullets */}
            <div className="lg:col-span-7 space-y-8">
              <div>
                <h2 className="text-3xl font-extrabold tracking-tight text-gray-900 uppercase">
                  Hvad vi tilbyder inden for {service.title.toLowerCase()}
                </h2>
                <p className="mt-4 text-gray-600 leading-relaxed text-lg">
                  Hos Bust Byg & Renovering ApS leverer vi altid professionelle løsninger tilpasset dine specifikke behov. Vi går op i detaljerne, overholder vores aftaler og forlader aldrig en opgave, før du er 100% tilfreds.
                </p>
              </div>

              {bullets.length > 0 && (
                <div className="space-y-4">
                  <h3 className="text-xl font-bold text-gray-900 uppercase">Vores ekspertise dækker:</h3>
                  <ul className="space-y-3">
                    {bullets.map((bullet, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <span className="mt-1 shrink-0 text-black">
                          <Check className="w-5 h-5" />
                        </span>
                        <span className="text-gray-700 font-medium">{bullet}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="pt-6 border-t border-gray-100">
                <p className="text-gray-500 text-sm">
                  {service.id === 'totalentreprise' ? (
                    "Vi samarbejder med autoriserede el-installatører til el-arbejde, så vi kan levere en komplet totalentreprise, hvor du kun har én kontaktperson."
                  ) : (
                    "Vi udfører alt inden for byggeri – undtagen el-arbejde (hvor vi samarbejder med autoriserede partnere)."
                  )}
                </p>
              </div>
            </div>

            {/* Right Column: CTA Box */}
            <div className="lg:col-span-5 bg-[#111111] border border-gray-800 p-8 rounded-none shadow-xl space-y-6 text-white">
              <h3 className="text-2xl font-bold text-white uppercase">Få et uforpligtende tilbud</h3>
              <p className="text-gray-400 text-sm leading-relaxed">
                Har du et projekt i tankerne? Uanset om det er stort eller småt, står vi klar til at rådgive dig og give et uforpligtende tilbud.
              </p>

              <div className="divide-y divide-gray-800 pt-2">
                <a
                  href={`tel:${companyData.phoneRaw}`}
                  className="flex items-center gap-3 py-4 hover:text-orange-500 transition-colors text-white font-bold"
                >
                  <Phone className="w-5 h-5 text-white shrink-0" />
                  <div className="text-left">
                    <div className="text-base">{companyData.phone}</div>
                  </div>
                </a>

                <a
                  href={`mailto:${companyData.email}`}
                  className="flex items-center gap-3 py-4 hover:text-orange-500 transition-colors text-white font-bold"
                >
                  <Mail className="w-5 h-5 text-white shrink-0" />
                  <div className="text-left">
                    <div className="text-base">{companyData.email}</div>
                  </div>
                </a>
              </div>

              <button
                onClick={() => {
                  navigate('/kontakt');
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                className="w-full bg-orange-500 hover:bg-orange-600 text-white py-4 px-6 font-bold uppercase tracking-wider text-sm transition-colors text-center rounded-none"
              >
                Gå til kontaktformular
              </button>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
}
